/**
 * Shared helpers for Student Withdrawal workflow.
 * API base: StudentWithdrawal/*
 */

export const WITHDRAWAL_ROLES = [
  "admission_officer",
  "branch_head",
  "finance_officer",
  "head_office",
];

export const STATUS_META = {
  draft: { label: "Draft", variant: "secondary", color: "#6c757d", step: "Draft — not submitted yet" },
  pending_branch_head: {
    label: "Pending Branch Head",
    variant: "warning",
    color: "#fd7e14",
    step: "Waiting for Branch Head review",
  },
  pending_admission: {
    label: "Pending Admission",
    variant: "info",
    color: "#0d6efd",
    step: "Waiting for Admission Officer update",
  },
  pending_finance: {
    label: "Pending Finance",
    variant: "primary",
    color: "#7c5dfa",
    step: "Waiting for Finance clearance",
  },
  pending_head_office: {
    label: "Pending Head Office",
    variant: "primary",
    color: "#5f3dc4",
    step: "Waiting for Head Office to finalize",
  },
  on_hold: {
    label: "On Hold",
    variant: "warning",
    color: "#ffc107",
    step: "Paused by Branch Head",
  },
  rejected: {
    label: "Rejected",
    variant: "danger",
    color: "#dc3545",
    step: "Closed — student will continue",
  },
  completed: {
    label: "Completed",
    variant: "success",
    color: "#28a745",
    step: "Student marked Left",
  },
};

export const TIMELINE_LABELS = {
  initiated: "Request Initiated",
  submitted: "Proceeded to Branch Head",
  proceeded_to_head_office: "Proceeded to Head Office",
  reassigned: "Reassigned",
  reviewed: "Reviewed",
  on_hold: "Put On Hold",
  resumed: "Resumed",
  rejected: "Rejected",
  completed: "Completed / Student Left",
  comment: "Comment",
};

export const LEAVING_CATEGORIES = [
  "Relocation",
  "Fee concerns",
  "Academic fit",
  "Transport",
  "Personal",
  "Other",
];

export const ACTION_TYPES = [
  { value: "comment", text: "Comment" },
  { value: "decision", text: "Decision" },
  { value: "call_parent", text: "Call Parent" },
  { value: "retention_attempt", text: "Retention Attempt" },
  { value: "other", text: "Other" },
];

/** Normalize login role → API role */
export function resolveWithdrawalRole(vm, roleSetup = null) {
  const q = (vm.$route && vm.$route.query && vm.$route.query.role) || "";
  if (WITHDRAWAL_ROLES.includes(String(q).toLowerCase())) {
    return String(q).toLowerCase();
  }

  const userID = Number(
    (vm.$store.state.userData && vm.$store.state.userData.userID) ||
      (vm.$store.state.userData && vm.$store.state.userData.localID) ||
      0
  );

  // Prefer campus role setup (multi-select assignments)
  if (roleSetup && userID) {
    const inList = (arr) =>
      Array.isArray(arr) && arr.map(Number).includes(userID);
    if (inList(roleSetup.admissionOfficerUserIDs)) return "admission_officer";
    if (inList(roleSetup.branchHeadUserIDs)) return "branch_head";
    if (inList(roleSetup.financeOfficerUserIDs)) return "finance_officer";
    if (inList(roleSetup.headOfficeUserIDs)) return "head_office";
  }

  const raw = String(
    (vm.$store.state.userData && vm.$store.state.userData.roles) ||
      (vm.$store.state.loggedUser && vm.$store.state.loggedUser.meta_role) ||
      ""
  ).toLowerCase();

  if (raw.includes("admission") || raw === "admission_officer") {
    return "admission_officer";
  }
  if (
    raw.includes("finance") ||
    raw.includes("account") ||
    raw === "finance_officer"
  ) {
    return "finance_officer";
  }
  if (
    raw.includes("branch") ||
    raw.includes("campus head") ||
    raw.includes("principal") ||
    raw === "branch_head" ||
    raw.includes("head office") ||
    raw.includes("administrator") ||
    raw.includes("admin")
  ) {
    return "branch_head";
  }
  return "admission_officer";
}

export function emptyRoleSetup() {
  return {
    headOfficeUserIDs: [],
    admissionOfficerUserIDs: [],
    branchHeadUserIDs: [],
    financeOfficerUserIDs: [],
  };
}

export function normalizeRoleSetupPayload(data = {}) {
  const toIdList = (arr, single) => {
    if (Array.isArray(arr) && arr.length) return arr.map(Number).filter((n) => n > 0);
    if (single != null && single !== "") {
      const n = Number(single);
      return n > 0 ? [n] : [];
    }
    return [];
  };
  return {
    headOfficeUserIDs: toIdList(
      data.headOfficeUserIDs,
      data.headOfficeUserID
    ),
    admissionOfficerUserIDs: toIdList(
      data.admissionOfficerUserIDs,
      data.admissionOfficerUserID
    ),
    branchHeadUserIDs: toIdList(
      data.branchHeadUserIDs,
      data.branchHeadUserID
    ),
    financeOfficerUserIDs: toIdList(
      data.financeOfficerUserIDs,
      data.financeOfficerUserID
    ),
  };
}

export function statusMeta(status) {
  return STATUS_META[status] || {
    label: status || "Unknown",
    variant: "secondary",
    color: "#6c757d",
    step: status || "",
  };
}

export function timelineLabel(eventType) {
  return TIMELINE_LABELS[eventType] || eventType || "Event";
}

export function daysOpen(createdOn) {
  if (!createdOn) return 0;
  const start = new Date(createdOn);
  if (Number.isNaN(start.getTime())) return 0;
  const diff = Date.now() - start.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Parent app / portal initiated withdrawal request */
export function isParentInitiatedWithdrawal(item = {}) {
  const header = item.header || item;
  return !!(
    header.parentInitiate ||
    header.parentInitiated ||
    header.ParentInitiate ||
    header.isParentInitiated ||
    item.parentInitiate ||
    item.parentInitiated ||
    item.ParentInitiate ||
    item.isParentInitiated
  );
}

/** Mixin: common params + API wrappers */
export const withdrawalMixin = {
  data() {
    return {
      roleSetup: emptyRoleSetup(),
      myRoles: [],
      primaryRole: null,
      /** null until GetRoleSetup returns; then drives New Withdrawal button */
      canCreateWithdrawalFlag: null,
      roleSetupLoaded: false,
    };
  },
  computed: {
    withdrawalRole() {
      if (
        this.primaryRole &&
        WITHDRAWAL_ROLES.includes(String(this.primaryRole))
      ) {
        return String(this.primaryRole);
      }
      return resolveWithdrawalRole(this, this.roleSetup);
    },
    withdrawalDb() {
      return this.$store.state.userData.db;
    },
    withdrawalCampusID() {
      return this.$store.state.userData.cId;
    },
    withdrawalUserID() {
      return (
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        0
      );
    },
    withdrawalToken() {
      return this.$store.state.userData.token;
    },
    withdrawalBase() {
      return `${this.$store.state.domain}StudentWithdrawal`;
    },
    canCreateWithdrawal() {
      if (this.canCreateWithdrawalFlag != null) {
        return !!this.canCreateWithdrawalFlag;
      }
      return this.isAssignedAdmission;
    },
    isAssignedAdmission() {
      return this.userInWithdrawalRoleList(
        this.roleSetup && this.roleSetup.admissionOfficerUserIDs
      );
    },
    isAssignedBranchHead() {
      return this.userInWithdrawalRoleList(
        this.roleSetup && this.roleSetup.branchHeadUserIDs
      );
    },
    isAssignedFinance() {
      return this.userInWithdrawalRoleList(
        this.roleSetup && this.roleSetup.financeOfficerUserIDs
      );
    },
    isAssignedHeadOffice() {
      return this.userInWithdrawalRoleList(
        this.roleSetup && this.roleSetup.headOfficeUserIDs
      );
    },
    /** Campus 10+ — Finance proceeds to Head Office instead of Mark Left */
    financeRequiresHeadOfficeProceed() {
      return Number(this.withdrawalCampusID) > 9;
    },
  },
  methods: {
    userInWithdrawalRoleList(list) {
      const uid = Number(this.withdrawalUserID || 0);
      if (!uid) return false;
      if (Array.isArray(list) && list.map(Number).includes(uid)) return true;
      return false;
    },
    hasWithdrawalRole(role) {
      const r = String(role || "");
      if (this.myRoles && this.myRoles.map(String).includes(r)) return true;
      if (this.primaryRole && String(this.primaryRole) === r) return true;
      if (r === "admission_officer") return this.isAssignedAdmission;
      if (r === "branch_head") return this.isAssignedBranchHead;
      if (r === "finance_officer") return this.isAssignedFinance;
      if (r === "head_office") return this.isAssignedHeadOffice;
      return false;
    },
    withdrawalQs(extra = {}) {
      const params = {
        db: this.withdrawalDb,
        campusID: this.withdrawalCampusID,
        role: this.withdrawalRole,
        ...extra,
      };
      return Object.keys(params)
        .filter(
          (k) =>
            params[k] !== undefined && params[k] !== null && params[k] !== ""
        )
        .map((k) => `${k}=${encodeURIComponent(params[k])}`)
        .join("&");
    },
    async withdrawalGet(path, extra = {}) {
      const url = `${this.withdrawalBase}/${path}?${this.withdrawalQs(extra)}`;
      return this.get({
        url,
        token: this.withdrawalToken,
        dashboard: true,
      });
    },
    async withdrawalPost(path, body = {}, extra = {}, opts = {}) {
      const url = `${this.withdrawalBase}/${path}?${this.withdrawalQs(extra)}`;
      return this.post({
        url,
        body,
        token: this.withdrawalToken,
        context: this,
        ...opts,
      });
    },
    applyRoleSetupResponse(data = {}) {
      this.roleSetup = normalizeRoleSetupPayload(data);
      this.myRoles = Array.isArray(data.myRoles)
        ? data.myRoles.map(String)
        : [];
      this.primaryRole = data.primaryRole
        ? String(data.primaryRole)
        : null;

      // If API did not send myRoles, derive from assigned user ID lists
      if (!this.myRoles.length) {
        const derived = [];
        if (this.isAssignedAdmission) derived.push("admission_officer");
        if (this.isAssignedBranchHead) derived.push("branch_head");
        if (this.isAssignedFinance) derived.push("finance_officer");
        if (this.isAssignedHeadOffice) derived.push("head_office");
        this.myRoles = derived;
      }
      if (!this.primaryRole && this.myRoles.length) {
        this.primaryRole = this.myRoles[0];
      }

      if (typeof data.canCreateWithdrawal === "boolean") {
        this.canCreateWithdrawalFlag = data.canCreateWithdrawal;
      } else if (this.isAssignedAdmission) {
        this.canCreateWithdrawalFlag = true;
      } else {
        this.canCreateWithdrawalFlag = false;
      }
      this.roleSetupLoaded = true;
    },
    /**
     * GET StudentWithdrawal/GetRoleSetup?db=&campusID=&userID=
     * Returns lists + optional myRoles, primaryRole, canCreateWithdrawal
     */
    async loadRoleSetup() {
      try {
        const res = await this.withdrawalGet("GetRoleSetup", {
          userID: this.withdrawalUserID,
        });
        const data = (res && res.data) || res || {};
        this.applyRoleSetupResponse(data);
      } catch (e) {
        console.error("GetRoleSetup failed:", e);
        this.roleSetup = emptyRoleSetup();
        this.myRoles = [];
        this.primaryRole = null;
        this.canCreateWithdrawalFlag = false;
        this.roleSetupLoaded = true;
        throw e;
      }
    },
    /**
     * POST StudentWithdrawal/SaveRoleSetup?db=&campusID=&userID=
     * Body: { headOfficeUserIDs, admissionOfficerUserIDs, branchHeadUserIDs, financeOfficerUserIDs }
     */
    async saveRoleSetup(payload) {
      const body = normalizeRoleSetupPayload(payload);
      await this.withdrawalPost("SaveRoleSetup", body, {
        userID: this.withdrawalUserID,
      });
      // Reload so myRoles / primaryRole / canCreateWithdrawal stay in sync
      await this.loadRoleSetup();
    },
    toastOk(msg) {
      this.$bvToast.toast(msg, {
        title: "Success",
        variant: "success",
        solid: true,
        toaster: "b-toaster-top-center",
      });
    },
    toastErr(msg) {
      this.$bvToast.toast(msg || "Something went wrong", {
        title: "Error",
        variant: "danger",
        solid: true,
        toaster: "b-toaster-top-center",
      });
    },
    extractList(res) {
      if (!res) return { items: [], total: 0 };
      const data = res.data !== undefined ? res.data : res;
      if (Array.isArray(data)) return { items: data, total: data.length };
      const items =
        data.items || data.records || data.list || data.data || [];
      const total =
        data.totalRecords ||
        data.totalCount ||
        data.total ||
        (Array.isArray(items) ? items.length : 0);
      return { items: Array.isArray(items) ? items : [], total };
    },
    extractDetail(res) {
      if (!res) return null;
      const data = res.data !== undefined ? res.data : res;
      if (data && data.header) return data;
      if (data && data.data && data.data.header) return data.data;
      return data;
    },
    statusBadgeClass(status) {
      return `sw-badge sw-badge-${status || "draft"}`;
    },
    statusLabel(status) {
      return statusMeta(status).label;
    },
    currentStepText(status) {
      return statusMeta(status).step;
    },
  },
};
